// src/store.ts
import axios, { AxiosInstance } from 'axios';
import { IFavoriteProduct } from 'interfaces/IProduct';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createAuthenticatedAxiosInstance, getFavorites } from '../api/auth';

/**
 * Interface for Userentication state
 */
interface UserState {
    userIdSelected: number | null;
    setUserSelected: (userIdSelected: number | null) => void;
    isEdited: boolean;
    setEdited: (isEdited: boolean) => void;
    /**
     * A list of favorite products 
     */
    favoriteProducts: IFavoriteProduct[];
    /**
     * A list of favorite products 
     */
    setFavoriteProducts: (products: IFavoriteProduct[]) => void;
    /**
     * Flag to indicate if favorites are loaded
     */
    favoritesLoaded: boolean;
    setFavoritesLoaded: (loaded: boolean) => void;
    /**
     * Function to load favorites from API
     */
    loadFavorites: (token: string) => Promise<void>;
    /**
     * Function to reset favorites data on logout
     */
    resetFavorites: () => void;
    /**
     * Function to force reload favorites
     */
    forceReloadFavorites: (token: string) => Promise<void>;
    imageTimestamp: number;
    setImageTimestamp: (timestamp: number) => void;
}

/**
 * Zustand store for User state with persistence for favorites
 */
export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      userIdSelected: null,
      setUserSelected: (userIdSelected) => set({ userIdSelected }),
      isEdited: false,
      setEdited: (isEdited) => set({ isEdited }),
      favoriteProducts: [],
      setFavoriteProducts: (favoriteProducts) => set({ favoriteProducts }),
      favoritesLoaded: false,
      setFavoritesLoaded: (favoritesLoaded) => set({ favoritesLoaded }),
      loadFavorites: async (token: string) => {
        try {
          const axiosInstance = createAuthenticatedAxiosInstance(token);
          const { data, success } = await getFavorites(axiosInstance);
          
          
          if (success && data) {
            set({ favoriteProducts: data, favoritesLoaded: true });
          } else {
            set({ favoritesLoaded: true }); // Mark as loaded even if empty to prevent infinite retries
          }
        } catch (error) {
          console.error('Error loading favorites:', error);
          set({ favoritesLoaded: true }); // Mark as loaded to prevent infinite retries
        }
      },
      resetFavorites: () => {
        set({ 
          favoriteProducts: [], 
          favoritesLoaded: false,
          userIdSelected: null,
          isEdited: false 
        });
      },
      forceReloadFavorites: async (token: string) => {
        set({ favoritesLoaded: false });
        try {
          const axiosInstance = createAuthenticatedAxiosInstance(token);
          const { data, success } = await getFavorites(axiosInstance);
          
          
          if (success && data) {
            set({ favoriteProducts: data, favoritesLoaded: true });
          } else {
            set({ favoritesLoaded: true }); // Mark as loaded even if empty
          }
        } catch (error) {
          console.error('Error force reloading favorites:', error);
          set({ favoritesLoaded: true }); // Mark as loaded to prevent infinite retries
        }
      },
      imageTimestamp: 0,
      setImageTimestamp: (timestamp) => set({ imageTimestamp: timestamp })
    }),
    {
      name: 'user-storage',
      partialize: (state) => ({ 
        favoriteProducts: state.favoriteProducts,
        favoritesLoaded: state.favoritesLoaded 
      })
    }
  )
);